import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui'
import { useUnitTranslation } from '@/contexts/TranslationContext'
import { useAuth } from '@/contexts/AuthContext'
import { useUnit } from '@/contexts/UnitContext'
import { getUnitMembers, removeUnitMember, updateUnitMemberRole } from '@/lib/units/members'
import { getUnitInvitations, inviteUserToUnit, cancelInvitation } from '@/lib/units/invitations'

import type { Role } from '@/lib/types'

interface TeamModalProps {
  unitId: string
  isOpen: boolean
  onClose: () => void
}

export function TeamModal({ unitId, isOpen, onClose }: TeamModalProps) {
  const { tu } = useUnitTranslation()
  const { user } = useAuth()
  const { userRole } = useUnit()

  const [members, setMembers] = useState<any[]>([])
  const [invitations, setInvitations] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState(false)

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Role>('viewer')
  const [inviting, setInviting] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const [m, inv] = await Promise.all([
        getUnitMembers(unitId),
        getUnitInvitations(unitId),
      ])
      setMembers(m)
      setInvitations(inv.filter((i: any) => i.status === 'pending'))
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) load()
  }, [isOpen, unitId])

  const flash = (msg: string, type: 'success' | 'error') => {
    if (type === 'success') { setSuccess(msg); setTimeout(() => setSuccess(null), 4000) }
    else { setError(msg); setTimeout(() => setError(null), 5000) }
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteEmail.trim()) return
    setInviting(true)
    try {
      await inviteUserToUnit(unitId, inviteEmail.trim(), inviteRole, userRole || undefined)
      setInviteEmail('')
      flash(tu('record.team.inviteSentSuccess'), 'success')
      await load()
    } catch (e: any) {
      const msg = e.message?.includes('already exists')
        ? tu('record.team.inviteAlreadyExists')
        : tu('record.team.inviteSendError')
      flash(msg, 'error')
    } finally {
      setInviting(false)
    }
  }

  const handleCancelInvite = async (id: string) => {
    try {
      await cancelInvitation(id, userRole || undefined)
      flash(tu('record.team.inviteCanceledSuccess'), 'success')
      await load()
    } catch (e: any) {
      flash(tu('record.team.inviteCancelError'), 'error')
    }
  }

  const handleRoleChange = async (memberId: string, role: Role) => {
    try {
      await updateUnitMemberRole(memberId, role, userRole || undefined)
      await load()
    } catch (e: any) {
      flash(e.message || 'Erro ao atualizar papel', 'error')
    }
  }

  const handleRemove = async (memberId: string, name: string) => {
    if (!confirm(`Remover ${name} do time?`)) return
    try {
      await removeUnitMember(memberId, userRole || undefined)
      flash(`${name} removido.`, 'success')
      await load()
    } catch (e: any) {
      flash(e.message || 'Erro ao remover', 'error')
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={tu('record.team.title', { unit: tu('singular') })}
      size="md"
      headerLeft={
        isMobile ? (
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
            aria-label="Voltar"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        ) : undefined
      }
      headerRight={
        !isMobile ? (
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
            aria-label="Fechar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : undefined
      }
      mobileTopAligned={false}
    >
      <div className="space-y-5">
        {/* Alerts */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-800/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/10 dark:border-green-800/30 px-4 py-3 text-sm text-green-700 dark:text-green-400">
            {success}
          </div>
        )}

        {/* Invite form */}
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {tu('record.team.inviteNewMember')}
          </h3>
          <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              required
              placeholder="email@exemplo.com"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <select
              value={inviteRole}
              onChange={e => setInviteRole(e.target.value as Role)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="viewer">{tu('list.viewer')}</option>
              <option value="operator">{tu('list.operator')}</option>
              <option value="manager">{tu('list.manager')}</option>
              <option value="admin">{tu('list.admin')}</option>
            </select>
            <button
              type="submit"
              disabled={inviting}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60 transition-colors"
            >
              {inviting ? tu('record.team.sending') : tu('record.team.sendInvite')}
            </button>
          </form>
        </div>

        <div className="border-t border-gray-100 dark:border-gray-700" />

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-green-600" />
          </div>
        ) : (
          <>
            {/* Members */}
            <div>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {tu('record.team.activeMembers')} · {members.length}
              </h3>
              {members.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">{tu('record.team.noMembers')}</p>
              ) : (
                <ul className="space-y-2">
                  {members.map((m) => {
                    const isSelf = m.user_id === user?.id
                    const name = m.full_name || m.email || m.user_id
                    return (
                      <li key={m.id} className="flex items-center gap-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 px-3 py-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-bold">
                          {(name[0] || '?').toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                            {name} {isSelf && <span className="text-xs text-gray-400 font-normal">(você)</span>}
                          </p>
                          {m.email && m.full_name && (
                            <p className="truncate text-xs text-gray-400">{m.email}</p>
                          )}
                        </div>
                        <select
                          value={m.role}
                          disabled={isSelf}
                          onChange={e => handleRoleChange(m.id, e.target.value as Role)}
                          className="rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-green-500"
                        >
                          <option value="viewer">{tu('list.viewer')}</option>
                          <option value="operator">{tu('list.operator')}</option>
                          <option value="manager">{tu('list.manager')}</option>
                          <option value="admin">{tu('list.admin')}</option>
                        </select>
                        {!isSelf && (
                          <button
                            onClick={() => handleRemove(m.id, name)}
                            className="rounded-md p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            title="Remover membro"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {/* Pending invitations */}
            <div>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {tu('record.team.pendingInvites')} · {invitations.length}
              </h3>
              {invitations.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">{tu('record.team.noInvites')}</p>
              ) : (
                <ul className="space-y-2">
                  {invitations.map((inv) => (
                    <li key={inv.id} className="flex items-center gap-3 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/20 px-3 py-2.5">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-400">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{inv.email}</p>
                        <p className="text-xs text-gray-400">
                          {tu(`record.roles.${inv.role}`) || inv.role} · {tu('list.as')} {tu('record.team.awaiting')}
                        </p>
                      </div>
                      <button
                        onClick={() => handleCancelInvite(inv.id)}
                        className="rounded-md p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title={tu('record.team.cancelInviteTitle')}
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
